import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import {
  ExternalTenantGuidelinesDetailDto,
  ExternalTenantGuidelinesService,
} from '@artemis-software/wr-api';
import { NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-tenant-guidelines-config',
  templateUrl: './tenant-guidelines-config.component.html',
  styleUrls: ['./tenant-guidelines-config.component.scss'],
})
export class TenantGuidelinesConfigComponent implements OnInit {
  private readonly guidelinesService = inject(ExternalTenantGuidelinesService);
  private readonly toastr = inject(NbToastrService);

  readonly externalTenantId = input<string>();
  readonly internalTenantId = input<string>();
  readonly canEdit = input(false);

  readonly clerkGuidelines = signal<ExternalTenantGuidelinesDetailDto | null>(null);
  readonly technicianGuidelines = signal<ExternalTenantGuidelinesDetailDto | null>(null);
  readonly saving = signal(false);

  readonly clerkInfoText = signal('');
  readonly clerkChecklistItems = signal<string[]>([]);
  readonly technicianInfoText = signal('');
  readonly technicianChecklistItems = signal<string[]>([]);

  readonly clerkHasData = computed(() =>
    this.clerkInfoText().trim().length > 0 ||
    this.clerkChecklistItems().some(i => i.trim().length > 0),
  );

  readonly technicianHasData = computed(() =>
    this.technicianInfoText().trim().length > 0 ||
    this.technicianChecklistItems().some(i => i.trim().length > 0),
  );

  async ngOnInit(): Promise<void> {
    const externalId = this.externalTenantId();
    const internalId = this.internalTenantId();
    if (!externalId || !internalId) return;

    const all = await firstValueFrom(
      this.guidelinesService.findByTenantPair(internalId, externalId),
    );

    const clerk = all.find(g => g.type === 'CLERK') ?? null;
    const technician = all.find(g => g.type === 'TECHNICIAN') ?? null;

    this.clerkGuidelines.set(clerk);
    this.clerkInfoText.set(clerk?.infoText ?? '');
    this.clerkChecklistItems.set([...(clerk?.checklistItems ?? [])]);

    this.technicianGuidelines.set(technician);
    this.technicianInfoText.set(technician?.infoText ?? '');
    this.technicianChecklistItems.set([...(technician?.checklistItems ?? [])]);
  }

  addClerkItem(): void {
    this.clerkChecklistItems.update(items => [...items, '']);
  }

  removeClerkItem(index: number): void {
    this.clerkChecklistItems.update(items => items.filter((_, i) => i !== index));
  }

  updateClerkItem(index: number, value: string): void {
    this.clerkChecklistItems.update(items => {
      const copy = [...items];
      copy[index] = value;
      return copy;
    });
  }

  addTechnicianItem(): void {
    this.technicianChecklistItems.update(items => [...items, '']);
  }

  removeTechnicianItem(index: number): void {
    this.technicianChecklistItems.update(items => items.filter((_, i) => i !== index));
  }

  updateTechnicianItem(index: number, value: string): void {
    this.technicianChecklistItems.update(items => {
      const copy = [...items];
      copy[index] = value;
      return copy;
    });
  }

  clearClerk(): void {
    this.clerkInfoText.set('');
    this.clerkChecklistItems.set([]);
  }

  clearTechnician(): void {
    this.technicianInfoText.set('');
    this.technicianChecklistItems.set([]);
  }

  async save(): Promise<void> {
    const externalId = this.externalTenantId();
    const internalId = this.internalTenantId();
    if (!externalId || !internalId) return;

    this.saving.set(true);
    try {
      await Promise.all([
        this.clerkHasData()
          ? firstValueFrom(this.guidelinesService.upsert({
            internalTenantId: internalId,
            externalTenantId: externalId,
            type: 'CLERK',
            infoText: this.clerkInfoText(),
            checklistItems: this.clerkChecklistItems().filter(i => i.trim().length > 0),
          })).then(result => {
            this.clerkGuidelines.set(result);
            this.clerkChecklistItems.set([...result.checklistItems]);
          })
          : this.clerkGuidelines()
            ? firstValueFrom(this.guidelinesService._delete(internalId, externalId, 'CLERK'))
              .then(() => this.clerkGuidelines.set(null))
            : Promise.resolve(),
        this.technicianHasData()
          ? firstValueFrom(this.guidelinesService.upsert({
            internalTenantId: internalId,
            externalTenantId: externalId,
            type: 'TECHNICIAN',
            infoText: this.technicianInfoText(),
            checklistItems: this.technicianChecklistItems().filter(i => i.trim().length > 0),
          })).then(result => {
            this.technicianGuidelines.set(result);
            this.technicianChecklistItems.set([...result.checklistItems]);
          })
          : this.technicianGuidelines()
            ? firstValueFrom(this.guidelinesService._delete(internalId, externalId, 'TECHNICIAN'))
              .then(() => this.technicianGuidelines.set(null))
            : Promise.resolve(),
      ]);
    } catch {
      this.toastr.danger('Fehler beim Speichern der Leitfäden.', 'Fehler');
    } finally {
      this.saving.set(false);
    }
  }
}
